import React, { useEffect, useState } from "react";
import { fmtDms } from "../components/solar/CoordinateCard";
import { InviaCommessaButton } from "../components/InviaCommessaButton";
import { EmailStorico } from "../components/EmailStorico";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, API, formatApiError, statoBadgeClass, statoLabel } from "../lib/api";
import { ArrowLeft, FileText, Share2, Download, Loader2, Copy, Trash2, Clock, CheckCircle2, RotateCcw, Pencil, Eye, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import SectionBlock from "../components/SectionBlock";
import MaterialiBlock from "../components/MaterialiBlock";
import NextcloudBlock from "../components/NextcloudBlock";
import PianificazioneBlock from "../components/PianificazioneBlock";
import CheckQualityBlock from "../components/CheckQualityBlock";
import ClienteFormModal from "../components/ClienteFormModal";
import WhatsappPanel from "../components/WhatsappPanel";
import EconomicoBlock from "../components/EconomicoBlock";
import PdfPreviewModal from "../components/PdfPreviewModal";
import InterventoPianificaButton from "../components/InterventoPianificaButton";
import InterventiLista from "../components/InterventiLista";
import SopralluogoDateBlock from "../components/SopralluogoDateBlock";
import { SopralluogoProgettoFV } from "../components/solar/SopralluogoProgettoFV";
import InstallazioneDateBlock from "../components/InstallazioneDateBlock";
import SopralluogoAttesaButton from "../components/SopralluogoAttesaButton";
import InviaPianificazioneButton from "../components/InviaPianificazioneButton";
import InstallazioneKoButton from "../components/InstallazioneKoButton";
import { useAuth } from "../context/AuthContext";
import { canDoAction, isFieldRole, canSeeMenu } from "../lib/menu";
import DicoBlock from "../components/DicoBlock";
import PraticheAllaccio from "../components/pratiche/PraticheAllaccio";

const LEGACY_ROLES = ["admin", "operatore", "tecnico", "magazzino", "visualizzatore"];

const TABS = [
  { id: "anagrafica", label: "Anagrafica", field: true },
  { id: "sopralluogo", label: "Sopralluogo", module: "sopralluogo" },
  { id: "installazione", label: "Installazione", module: "installazione", field: true },
  { id: "intervento", label: "Interventi", module: "intervento" },
  { id: "pratiche", label: "Pratiche allaccio", field: true },
  { id: "pianificazione", label: "Pianificazione" },
  { id: "whatsapp", label: "WhatsApp", roles: LEGACY_ROLES },
  { id: "check_quality", label: "Check Quality", roles: LEGACY_ROLES },
  { id: "economico", label: "Economico", roles: ["admin", "operatore"] },
  { id: "dico", label: "DiCo", roles: ["admin", "operatore"] },
  { id: "email", label: "Email", roles: ["admin", "operatore", "tecnico"] },
];

export default function ClienteDetail() {
  const { cid } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cliente, setCliente] = useState(null);
  const [constants, setConstants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("anagrafica");
  const [waAutoTemplate, setWaAutoTemplate] = useState(null);
  const [sopralluogo, setSopralluogo] = useState(null);
  const [installazione, setInstallazione] = useState(null);
  const [intervento, setIntervento] = useState(null);
  const [filesSopra, setFilesSopra] = useState([]);
  const [filesInst, setFilesInst] = useState([]);
  const [filesInter, setFilesInter] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pianificazione, setPianificazione] = useState(null);

  const loadPianificazione = async () => {
    try {
      const { data } = await api.get(`/clienti/${cid}/pianificazione`);
      setPianificazione(data);
    } catch { /* */ }
  };

  useEffect(() => { loadPianificazione(); /* eslint-disable-next-line */ }, [cid]);

  // Deep-link: /clienti/{cid}?wa=variazione → apri tab WhatsApp e pre-compila template
  const tabParam = searchParams.get("tab");
  useEffect(() => {
    const def = TABS.find((x) => x.id === tabParam);
    if (def && (!def.roles || def.roles.includes(user?.role)) && (!isFieldRole(user) || def.field)) setTab(tabParam);
    // eslint-disable-next-line
  }, [cid, tabParam]);

  useEffect(() => {
    const wa = searchParams.get("wa");
    if (wa) {
      setTab("whatsapp");
      setWaAutoTemplate(wa);
      // Pulisci il query param dopo aver applicato il deep-link
      const next = new URLSearchParams(searchParams);
      next.delete("wa");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadAll = async () => {
    try {
      const [cRes, constRes, comRes] = await Promise.all([
        api.get(`/clienti/${cid}`),
        api.get("/constants"),
        api.get("/commesse"),
      ]);
      setCliente(cRes.data);
      setConstants(constRes.data);
      setCommesse(comRes.data);
      const mods = cRes.data.commessa?.modules || {};
      const promises = [api.get(`/clienti/${cid}/share`)];
      if (mods.sopralluogo) {
        promises.push(api.get(`/clienti/${cid}/sopralluogo`));
        promises.push(api.get(`/clienti/${cid}/sopralluogo/files`));
      }
      if (mods.installazione) {
        promises.push(api.get(`/clienti/${cid}/installazione`));
        promises.push(api.get(`/clienti/${cid}/installazione/files`));
      }
      if (mods.intervento) {
        promises.push(api.get(`/clienti/${cid}/intervento`));
        promises.push(api.get(`/clienti/${cid}/intervento/files`));
      }
      const results = await Promise.all(promises);
      setShareLinks(results[0].data);
      let i = 1;
      if (mods.sopralluogo) {
        setSopralluogo(results[i++].data);
        setFilesSopra(results[i++].data);
      }
      if (mods.installazione) {
        setInstallazione(results[i++].data);
        setFilesInst(results[i++].data);
      }
      if (mods.intervento) {
        setIntervento(results[i++].data);
        setFilesInter(results[i++].data);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [cid]);

  if (loading) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  if (!cliente) return <div>Cliente non trovato</div>;

  const mods = cliente.commessa?.modules || { sopralluogo: true, installazione: true, intervento: true };
  const availableTabs = TABS.filter((t) =>
    (!t.module || mods[t.module]) &&
    (!t.roles || t.roles.includes(user?.role)) &&
    (!isFieldRole(user) || t.field) &&
    (t.id !== "economico" || user?.role === "operatore" || canDoAction(user, "economico_view")) &&
    (t.id !== "dico" || user?.role === "admin" || canSeeMenu(user, "dico"))
  );

  const tokenParam = () => {
    const t = localStorage.getItem("access_token");
    return t ? `?auth=${encodeURIComponent(t)}` : "";
  };

  const openPdf = (module) => {
    const url = `${API}/clienti/${cid}/${module}/pdf${tokenParam()}`;
    window.open(url, "_blank");
  };

  const createShareLink = async (module) => {
    try {
      // expires_in_days=0 → link permanente (non scade mai)
      const { data } = await api.post(`/clienti/${cid}/share`, { module, expires_in_days: 0 });
      const url = data.public_url || `${window.location.origin}/public/${data.token}`;
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success(data.existing ? "Link già esistente copiato negli appunti" : "Link creato e copiato negli appunti");
      const sRes = await api.get(`/clienti/${cid}/share`);
      setShareLinks(sRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const revokeLink = async (token) => {
    if (!window.confirm("Revocare il link?")) return;
    try {
      await api.delete(`/share/${token}`);
      setShareLinks(shareLinks.filter((l) => l.token !== token));
      toast.success("Link revocato");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyLink = async (link) => {
    const url = link.public_url || `${window.location.origin}/public/${link.token}`;
    await navigator.clipboard.writeText(url);
    toast.success("Copiato");
  };

  const makePermanent = async (token) => {
    try {
      await api.put(`/share/${token}/permanent`);
      setShareLinks(shareLinks.map((l) => l.token === token ? { ...l, expires_at: null } : l));
      toast.success("Link reso permanente");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="cliente-detail">
      <Link to="/clienti" className="inline-flex items-center gap-1.5 text-sm mb-4 hover:underline" style={{ color: "var(--text-muted)" }}>
        <ArrowLeft className="w-4 h-4" /> Torna ai clienti
      </Link>

      <div className="card-shell p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: "var(--text-muted)" }}>
              {cliente.cliente_id} · {cliente.commessa?.name || "—"}
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
              {cliente.nome} {cliente.cognome_ragione_sociale}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm" style={{ color: "var(--text-muted)" }}>
              {cliente.telefono && <span>📞 {cliente.telefono}</span>}
              {cliente.email && <span>✉️ {cliente.email}</span>}
              {cliente.citta && <span>📍 {cliente.citta} ({cliente.provincia})</span>}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={statoBadgeClass(cliente.stato_pratica) + " text-sm px-3 py-1"}>
              {statoLabel(cliente.stato_pratica)}
            </span>
            {cliente.flusso_installazione === "attesa_conferma" && (
              <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="badge-attesa-conferma">
                In attesa conferma installazione da fornitore
              </span>
            )}
            {cliente.flusso_installazione === "da_pianificare" && (
              <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: "#DBEAFE", color: "#1E40AF" }} data-testid="badge-da-pianificare">
                Inviato in Installazioni da Pianificare
              </span>
            )}
            {cliente.flusso_installazione === "ko" && (
              <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid="badge-ko">
                Installazione KO
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="grid grid-cols-3 sm:grid-cols-4 md:flex md:flex-wrap gap-1 mb-6 p-1 rounded-lg md:w-fit" style={{ background: "var(--sidebar)" }} data-testid="cliente-tabs">
        {availableTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="px-2 md:px-4 py-2 text-xs md:text-sm font-medium rounded-md transition-all text-center leading-tight"
            style={tab === t.id ? { background: "#fff", color: "var(--text)", boxShadow: "0 1px 2px rgba(0,0,0,0.06)" } : { color: "var(--text-muted)" }}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "anagrafica" && (
        <div className="space-y-5" data-testid="tab-anagrafica-content">
          <NextcloudBlock cid={cid} />
          <div className="card-shell p-6">
            <div className="flex items-start justify-between mb-4 gap-3">
              <h2 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Dati anagrafici</h2>
              {!isFieldRole(user) && (
              <button
                onClick={() => setShowEdit(true)}
                className="btn-secondary text-xs"
                data-testid="edit-anagrafica"
                title="Modifica i dati anagrafici di questo cliente"
              >
                <Pencil className="w-3.5 h-3.5" /> Modifica
              </button>
              )}
            </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <Info label="Nome" value={cliente.nome} />
            <Info label="Cognome / Ragione sociale" value={cliente.cognome_ragione_sociale} />
            <Info label="Telefono" value={cliente.telefono} />
            <Info label="Email" value={cliente.email} />
            <Info label="Indirizzo" value={cliente.indirizzo} />
            <Info label="Città" value={cliente.citta} />
            <Info label="CAP" value={cliente.cap} />
            <Info label="Provincia" value={cliente.provincia} />
            {cliente.coord_lat != null && (
              <div data-testid="cliente-coordinate">
                <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--text-muted)" }}>Coordinate abitazione</div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${cliente.coord_lat},${cliente.coord_lng}`} target="_blank" rel="noreferrer"
                  className="font-mono font-semibold underline" style={{ color: "var(--primary)" }}>{Number(cliente.coord_lat).toFixed(6)}, {Number(cliente.coord_lng).toFixed(6)}</a>
                <div className="text-[11px] font-mono" style={{ color: "var(--text-muted)" }}>{fmtDms(cliente.coord_lat, cliente.coord_lng)}</div>
              </div>
            )}
            <Info label="CF / P.IVA" value={cliente.cf_piva} />
            <Info label="Commessa" value={cliente.commessa?.name} />
            <Info label="Stato pratica" value={statoLabel(cliente.stato_pratica)} />
            <Info label="Creato il" value={new Date(cliente.created_at).toLocaleDateString("it-IT")} />
          </div>
          {cliente.note && (
            <div className="mt-6 p-4 rounded-md" style={{ background: "var(--surface-muted)" }}>
              <div className="text-xs uppercase tracking-wider font-semibold mb-1" style={{ color: "var(--text-muted)" }}>Note</div>
              <div className="text-sm whitespace-pre-wrap">{cliente.note}</div>
            </div>
          )}
        </div>
        </div>
      )}

      {tab === "sopralluogo" && (
        <div className="space-y-5">
          <SopralluogoDateBlock
            cid={cid}
            initialDate={sopralluogo?.data_sopralluogo}
            initialEsito={sopralluogo?.esito_sopralluogo}
            readOnly={isFieldRole(user)}
            onSaved={loadAll}
          />
          <SopralluogoAttesaButton
            cid={cid}
            flusso={cliente.flusso_installazione}
            sopraDone={(constants?.sopralluogo_sections || []).length > 0 && (constants?.sopralluogo_sections || []).every((s) => sopralluogo?.sections?.[s.slug]?.completed)}
            onSaved={loadAll}
          />
          {!isFieldRole(user) && ["admin", "operatore", "tecnico"].includes(user?.role) && (
            <SopralluogoProgettoFV cliente={cliente} onChanged={loadAll} />
          )}
          <ModuleView
            title="Sopralluogo"
            testid="tab-sopralluogo-content"
            sections={constants?.sopralluogo_sections || []}
            files={filesSopra}
            sectionsData={sopralluogo?.sections || {}}
            cid={cid}
            module="sopralluogo"
            onGeneratePdf={() => openPdf("sopralluogo")}
            onPreviewPdf={() => setPdfPreview({ module: "sopralluogo", title: "Sopralluogo" })}
            readOnly={isFieldRole(user)}
            hideActions={isFieldRole(user)}
            onCreateShare={() => createShareLink("sopralluogo")}
            shareLinks={shareLinks.filter((l) => l.module === "sopralluogo")}
            onCopyLink={copyLink}
            onRevoke={revokeLink}
            onMakePermanent={makePermanent}
            onChanged={loadAll}
          />
        </div>
      )}

      {tab === "installazione" && (
        <div className="space-y-5">
          <InstallazioneDateBlock
            cid={cid}
            initialDate={installazione?.data_installazione}
            onSaved={loadAll}
          />
          <InviaPianificazioneButton
            cid={cid}
            flusso={cliente.flusso_installazione}
            onSaved={loadAll}
          />
          <InstallazioneKoButton
            cid={cid}
            flusso={cliente.flusso_installazione}
            onSaved={loadAll}
          />
          <ModuleView
            title="Installazione"
            testid="tab-installazione-content"
            sections={constants?.installazione_sections || []}
            files={filesInst}
            sectionsData={installazione?.sections || {}}
            cid={cid}
            module="installazione"
            onGeneratePdf={() => openPdf("installazione")}
            onPreviewPdf={() => setPdfPreview({ module: "installazione", title: "Installazione" })}
            hideActions={isFieldRole(user)}
            onCreateShare={() => createShareLink("installazione")}
            shareLinks={shareLinks.filter((l) => l.module === "installazione")}
            onCopyLink={copyLink}
            onRevoke={revokeLink}
            onMakePermanent={makePermanent}
            onChanged={loadAll}
            extraBlock={(
              <MaterialiBlock
                cid={cid}
                commessaId={cliente.commessa_id}
                categorie={constants?.materiali_categorie || []}
                initialMateriali={installazione?.materiali || []}
                onSaved={loadAll}
              />
            )}
          />
        </div>
      )}

      {tab === "intervento" && (
        <div className="space-y-5" data-testid="tab-intervento-content">
          <InterventoPianificaButton cid={cid} />
          <InterventiLista cid={cid} user={user} restricted={isFieldRole(user)} />
        </div>
      )}

      {tab === "pratiche" && (
        <div data-testid="tab-pratiche-content">
          <PraticheAllaccio cid={cid} cliente={cliente} canEdit={["admin", "operatore"].includes(user?.role)} />
        </div>
      )}

      {tab === "pianificazione" && (
        <div data-testid="tab-pianificazione-content">
          <PianificazioneBlock cid={cid} cliente={cliente} onSaved={loadPianificazione} restricted={isFieldRole(user)} />
        </div>
      )}

      {tab === "whatsapp" && (
        <div data-testid="tab-whatsapp-content">
          <WhatsappPanel cid={cid} cliente={cliente} pianificazione={pianificazione} autoTemplate={waAutoTemplate} onAutoTemplateHandled={() => setWaAutoTemplate(null)} />
        </div>
      )}

      {tab === "check_quality" && (
        <div data-testid="tab-check-quality-content">
          <CheckQualityBlock cid={cid} />
        </div>
      )}

      {tab === "economico" && (user?.role === "operatore" || (user?.role === "admin" && canDoAction(user, "economico_view"))) && (
        <EconomicoBlock cid={cid} />
      )}

      {tab === "dico" && (user?.role === "admin" || canSeeMenu(user, "dico")) && (
        <div data-testid="tab-dico-content">
          <DicoBlock cid={cid} cliente={cliente} user={user} />
        </div>
      )}

      {tab === "email" && ["admin", "operatore", "tecnico"].includes(user?.role) && <EmailStorico cid={cid} />}

      {showEdit && (
        <ClienteFormModal
          open={showEdit}
          cliente={cliente}
          commesse={commesse}
          onClose={() => setShowEdit(false)}
          onSaved={loadAll}
        />
      )}

      {pdfPreview && (
        <PdfPreviewModal
          cid={cid}
          module={pdfPreview.module}
          title={pdfPreview.title}
          onClose={() => setPdfPreview(null)}
        />
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="mt-0.5 font-medium">{value || "—"}</div>
    </div>
  );
}

function ModuleView({ title, testid, sections, files, sectionsData, cid, module, onGeneratePdf, onPreviewPdf, onCreateShare, shareLinks, onCopyLink, onRevoke, onMakePermanent, onChanged, extraBlock, readOnly = false, hideActions = false }) {
  const { user } = useAuth();
  const totalSections = sections.length;
  const completedCount = sections.reduce((acc, s) => acc + (sectionsData[s.slug]?.completed ? 1 : 0), 0);
  const allDone = totalSections > 0 && completedCount === totalSections;
  const progressPct = totalSections > 0 ? Math.round((completedCount / totalSections) * 100) : 0;
  const showBulkButton = totalSections >= 2;
  const [bulkLoading, setBulkLoading] = useState(false);

  const toggleAll = async () => {
    const nextCompleted = !allDone;
    if (nextCompleted) {
      if (!window.confirm(`Segnare come completate tutte le ${totalSections} sezioni di ${title}?`)) return;
    } else {
      if (!window.confirm(`Sbloccare tutte le ${totalSections} sezioni di ${title}?`)) return;
    }
    setBulkLoading(true);
    try {
      await api.put(`/clienti/${cid}/${module}/sections/complete-all`, { completed: nextCompleted });
      toast.success(nextCompleted ? `Tutte le sezioni di ${title} segnate come completate` : `Tutte le sezioni di ${title} sbloccate`);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid={testid}>
      <div className="card-shell p-5 flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h2 className="font-bold text-xl" style={{ fontFamily: "Manrope" }}>{title}</h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {sections.length} sezioni · {files.length} file caricati
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!hideActions && canDoAction(user, "pdf_generate") && (
          <>
          <button onClick={onPreviewPdf} className="btn-secondary" data-testid={`pdf-preview-${module}`}>
            <Eye className="w-4 h-4" /> Anteprima PDF
          </button>
          <button onClick={onGeneratePdf} className="btn-accent" data-testid={`pdf-${module}`}>
            <FileText className="w-4 h-4" /> Genera PDF
          </button>
          </>
          )}
          {!hideActions && (
          <button onClick={onCreateShare} className="btn-primary" data-testid={`share-${module}`}>
            <Share2 className="w-4 h-4" /> Link condivisibile
          </button>
          )}
          {!hideActions && ["sopralluogo", "installazione"].includes(module) && ["admin", "operatore", "tecnico"].includes(user?.role) && (
            <InviaCommessaButton cid={cid} module={module} allDone={allDone} />
          )}
        </div>
      </div>

      {/* Mini-progress + bulk complete (solo moduli a ≥2 sezioni) */}
      {showBulkButton && (
        <div
          className="card-shell p-4 flex flex-wrap items-center gap-4"
          data-testid={`module-progress-${module}`}
        >
          <div className="flex-1 min-w-[220px]">
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-xs uppercase font-bold tracking-wider" style={{ color: "var(--text-muted)" }}>
                Avanzamento sezioni
              </span>
              <span className="text-sm font-bold" style={{ color: allDone ? "#065F46" : "var(--text)" }} data-testid={`module-progress-count-${module}`}>
                {completedCount} di {totalSections} completate · {progressPct}%
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--surface-muted)" }}>
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${progressPct}%`,
                  background: allDone ? "#10B981" : "var(--primary)",
                }}
              />
            </div>
          </div>
          {!readOnly && (
          <button
            onClick={toggleAll}
            disabled={bulkLoading}
            className={allDone ? "btn-secondary" : "btn-primary"}
            data-testid={`bulk-complete-${module}`}
          >
            {bulkLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : allDone ? (
              <><RotateCcw className="w-4 h-4" /> Sblocca tutto</>
            ) : (
              <><CheckCircle2 className="w-4 h-4" /> Completa tutto</>
            )}
          </button>
          )}
        </div>
      )}

      {!hideActions && shareLinks.length > 0 && (
        <div className="card-shell p-4">
          <div className="font-semibold text-sm mb-2">Link attivi ({shareLinks.length})</div>
          <div className="space-y-1.5">
            {shareLinks.map((l) => {
              const url = l.public_url || `${window.location.origin}/public/${l.token}`;
              return (
                <div key={l.token} className="flex items-center gap-2 text-xs p-2 rounded-md" style={{ background: "var(--surface-muted)" }}>
                  <Clock className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} />
                  <code className="flex-1 truncate">{url}</code>
                  {l.expires_at ? (
                    <>
                      <span style={{ color: "var(--text-muted)" }}>scade {new Date(l.expires_at).toLocaleDateString("it-IT")}</span>
                      <button
                        onClick={() => onMakePermanent(l.token)}
                        className="px-1.5 py-0.5 rounded text-[10px] font-semibold hover:bg-black/5"
                        style={{ background: "#E6F4EA", color: "#1B5E20" }}
                        title="Rimuovi la scadenza"
                        data-testid={`make-permanent-${l.token}`}
                      >
                        Rendi permanente
                      </button>
                    </>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded-full font-semibold" style={{ background: "#E6F4EA", color: "#1B5E20" }}>permanente</span>
                  )}
                  <button onClick={() => window.open(`${window.location.origin}/public/${l.token}`, "_blank")} className="p-1 rounded hover:bg-black/5" title="Apri il link in un'altra pagina" data-testid={`open-link-${l.token}`}><ExternalLink className="w-3.5 h-3.5" style={{ color: "var(--primary)" }} /></button>
                  <button onClick={() => onCopyLink(l)} className="p-1 rounded hover:bg-black/5" title="Copia link"><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onRevoke(l.token)} className="p-1 rounded hover:bg-black/5"><Trash2 className="w-3.5 h-3.5" style={{ color: "#B30000" }} /></button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {sections.map((s) => (
          <SectionBlock
            key={s.slug}
            cid={cid}
            module={module}
            section={s}
            initialNotes={sectionsData[s.slug]?.notes || ""}
            initialFiles={files.filter((f) => f.section === s.slug)}
            initialCompleted={!!sectionsData[s.slug]?.completed}
            initialCompletedBy={sectionsData[s.slug]?.completed_by_name || ""}
            initialCompletedAt={sectionsData[s.slug]?.completed_at || ""}
            onChanged={onChanged}
            readOnly={readOnly}
          />
        ))}
      </div>

      {extraBlock}
    </div>
  );
}
